import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  cn,
  GridPattern,
  RainbowButton,
} from '@data-delivery/d3s';
import { useAuth } from '@data-delivery/okta-auth-react';
import { LockOpenIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { features } from '../../components/web-info-slides';
import { BrandLogo } from '../../layout/components/brand-logo/brand-logo';

export function Login({ authPath }: { authPath: string }) {
  const { isAuthenticated, login, setOriginalUri } = useAuth();
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (redirecting || isAuthenticated) {
      return;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const from = params.get('from');
      if (from && from.startsWith('/') && !from.startsWith('/login') && !from.startsWith('/auth')) {
        setOriginalUri(from);
      }
    } catch {}
  }, [isAuthenticated, redirecting, setOriginalUri]);

  const handleRedirect = async () => {
    try {
      if (redirecting) {
        return;
      }

      setRedirecting(true);
      await login();
    } catch {
      setRedirecting(false);
    }
  };

  if (isAuthenticated) {
    return <Navigate to='/' />;
  }

  return (
    <div className='container relative h-full flex-col items-center justify-center grid lg:max-w-none lg:grid-cols-2 lg:px-0 overflow-hidden'>
      <div className='relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex'>
        <div className='absolute inset-0 w-full h-full bg-slate-900 pointer-events-none' />
        <GridPattern
          numSquares={30}
          maxOpacity={0.1}
          duration={3}
          className={cn(
            '[mask-image:radial-gradient(500px_circle_at_center,white,transparent)]',
            'inset-x-0 inset-y-[-30%] h-[200%] skew-y-12',
          )}
        />
        <BrandLogo
          text='PC SFTP Permissioning UI'
          logoClassName='w-[140px] text-white'
          textClassName='text-lg font-medium text-white before:bg-white'
          url='/login'
        />
        <div className='mt-auto mx-auto flex w-full flex-col justify-center space-y-5 sm:w-[90%]'>
          <Carousel opts={{ loop: true }} className='w-full'>
            <CarouselContent>
              {features.map(feature => (
                <CarouselItem key={crypto.randomUUID()}>
                  <div className='flex flex-col aspect-auto text-left justify-center p-6'>
                    <h2 className='font-semibold text-xl text-left mb-5'>{feature.title}</h2>
                    <div className='text-sm leading-6 font-thin'>{feature.description}</div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious />
            <CarouselNext />
          </Carousel>
        </div>
      </div>
      <div className='lg:p-8'>
        <div className='mx-auto flex flex-col justify-center space-y-5 w-[450px]'>
          <div className='flex flex-col space-y-7 text-center'>
            <h1 className='text-2xl mb-4 font-semibold tracking-tight'>Login</h1>
            <p className='mb-5'>
              Click below to securely log in through organization&apos;s SSO portal.
            </p>
            <div className='grid gap-3'>
              <RainbowButton type='button' onClick={handleRedirect} disabled={redirecting}>
                <LockOpenIcon className='mr-2 h-4 w-4' />
                {redirecting ? 'Redirecting…' : 'Login Using Single-sign On'}
              </RainbowButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
